import { Link } from 'react-router-dom';
import { Lock, Loader2, RefreshCw } from 'lucide-react';
import { useAccess, requiredLevel, tierOf, isBrokerTier } from '@/components/dashboard/access';

/*
  Bloqueo elegante de secciones por nivel (decisión de Samuel, 09/10/2026).

  Las secciones no permitidas no aparecen en el menú (DashboardLayout); si alguien entra por URL,
  ve este bloqueo con el mensaje de su caso. El servidor aplica la misma matriz (403), así que no
  se descarga ningún dato ajeno. Sin difuminados con datos falsos: solo Oportunidades tiene vista
  censurada (MembersOnly).
*/

// Compatibilidad: comprobación directa de broker Premium (o equipo)
export function hasPremiumAccess({ user, membership, isAdmin }) {
  if (!user) return false;
  return isBrokerTier(tierOf({ user, membership, isAdmin }));
}

const LOCKS = {
  broker: {
    title: 'Herramienta exclusiva para brokers Premium',
    text: 'La membresía Premium incluye el Bootcamp, la Certificación FHC y la habilitación como broker. Con ella se desbloquean las herramientas de trabajo con tu red.',
    cta: 'Ver membresía Premium',
  },
  member: {
    title: 'Sección exclusiva para miembros del club',
    text: 'Con cualquier membresía (Access, PRO o Premium) accedes a esta sección, a las oportunidades del club y al resto del área privada.',
    cta: 'Ver membresías',
  },
};

export function GateLoading() {
  return (
    <div className="flex items-center justify-center py-24 text-gray-400 text-sm gap-2" data-testid="access-gate-loading">
      <Loader2 size={16} className="animate-spin" /> Comprobando tu membresía…
    </div>
  );
}

export function GateError({ onRetry }) {
  return (
    <div className="max-w-md mx-auto text-center py-20" data-testid="access-gate-error">
      <p className="text-sm text-gray-400 mb-4">No hemos podido comprobar tu membresía. Inténtalo de nuevo en unos segundos.</p>
      <button type="button" onClick={onRetry}
        className="inline-flex items-center gap-2 min-h-[40px] px-4 py-2 rounded-lg border border-white/10 text-sm text-gray-300 hover:text-white hover:border-[#c9a84c]/40">
        <RefreshCw size={14} /> Reintentar
      </button>
    </div>
  );
}

export function LockedPanel({ level = 'broker', section }) {
  const copy = LOCKS[level] || LOCKS.member;
  return (
    <div className="max-w-lg mx-auto text-center py-14 px-6 bg-white/[0.02] border border-white/[0.06] rounded-2xl" data-testid={`access-gate-locked-${level}`}>
      <div className="w-12 h-12 mx-auto mb-4 rounded-full bg-[#c9a84c]/10 border border-[#c9a84c]/30 flex items-center justify-center">
        <Lock size={20} className="text-[#c9a84c]" />
      </div>
      {section && <p className="text-[11px] text-[#c9a84c] uppercase tracking-wider font-semibold mb-2">{section}</p>}
      <h2 className="text-white text-lg font-semibold mb-2">{copy.title}</h2>
      <p className="text-sm text-gray-400 leading-relaxed mb-6">{copy.text}</p>
      <div className="flex flex-col sm:flex-row gap-3 justify-center">
        <Link to="/dashboard#membresias"
          className="inline-flex items-center justify-center min-h-[40px] px-5 py-2.5 rounded-lg bg-[#c9a84c] text-[#060d1b] text-sm font-semibold hover:bg-[#d8b85c]">
          {copy.cta}
        </Link>
        <Link to="/dashboard"
          className="inline-flex items-center justify-center min-h-[40px] px-5 py-2.5 rounded-lg border border-white/10 text-sm text-gray-300 hover:text-white hover:border-[#c9a84c]/40">
          Volver al inicio
        </Link>
      </div>
    </div>
  );
}

/** Puerta genérica: <AccessGate feature="network" section="Red de afiliados">…</AccessGate> */
export function AccessGate({ feature, section, children }) {
  const { can, loading, error, refreshMembership } = useAccess();
  if (can(feature)) return children;
  if (loading) return <GateLoading />;
  if (error) return <GateError onRetry={refreshMembership} />;
  return <LockedPanel level={requiredLevel(feature)} section={section} />;
}

/** Atajo histórico (Calculadora): herramienta de broker Premium. */
export default function PremiumOnly({ feature = 'calculator', section, children }) {
  return <AccessGate feature={feature} section={section}>{children}</AccessGate>;
}
